import type { ApolloClient } from "@apollo/client";
import {
  GROUP_EMAIL_COMMITTEE_MAX_PAGES,
  GROUP_EMAIL_COMMITTEE_PAGE_SIZE,
  GROUP_EMAIL_SECTIONS_MAX_PAGES,
  GROUP_EMAIL_SECTIONS_PAGE_SIZE
} from "../constants";
import { BACKOFFICE_COMMITTEES_QUERY, CRM_SECTIONS_FOR_GROUP_EMAIL_QUERY } from "../queries";
import type {
  BackofficeCommitteesQueryResponse,
  BackofficeCommitteesQueryVariables,
  CommitteeNode,
  CrmSectionOption,
  CrmSectionsForGroupEmailQueryResponse,
  CrmSectionsForGroupEmailQueryVariables
} from "../types";

export type GroupEmailCommitteeOption = Pick<CommitteeNode, "id" | "name">;

export async function fetchCrmSectionsForGroupEmail(client: ApolloClient<object>): Promise<{
  sections: CrmSectionOption[];
  truncated: boolean;
}> {
  const sections: CrmSectionOption[] = [];
  let after: string | undefined;
  let truncated = false;
  let pages = 0;

  while (pages < GROUP_EMAIL_SECTIONS_MAX_PAGES) {
    const variables: CrmSectionsForGroupEmailQueryVariables = {
      first: GROUP_EMAIL_SECTIONS_PAGE_SIZE,
      ...(after ? { after } : {})
    };
    const { data } = await client.query<CrmSectionsForGroupEmailQueryResponse, CrmSectionsForGroupEmailQueryVariables>({
      query: CRM_SECTIONS_FOR_GROUP_EMAIL_QUERY,
      variables,
      fetchPolicy: "no-cache"
    });

    sections.push(...(data.crmSections.nodes ?? []));
    pages += 1;

    const pageInfo = data.crmSections.pageInfo;
    const nextAfter = pageInfo.endCursor ?? undefined;
    if (!pageInfo.hasNextPage) {
      return { sections, truncated };
    }
    if (!nextAfter || nextAfter === after) {
      return { sections, truncated: true };
    }
    after = nextAfter;
  }

  truncated = true;
  return { sections, truncated };
}

export async function fetchActiveCommitteesForGroupEmail(
  client: ApolloClient<object>,
  { sectionId, core }: { sectionId: string; core: boolean }
): Promise<{ committees: GroupEmailCommitteeOption[]; truncated: boolean }> {
  const committees: GroupEmailCommitteeOption[] = [];
  let after: string | undefined;
  let truncated = false;
  let pages = 0;

  while (pages < GROUP_EMAIL_COMMITTEE_MAX_PAGES) {
    const variables: BackofficeCommitteesQueryVariables = {
      first: GROUP_EMAIL_COMMITTEE_PAGE_SIZE,
      after,
      input: {
        sectionId,
        status: "ACTIVE",
        ...(core ? { core: true } : {})
      },
      orderBy: { field: "NAME", direction: "ASC" }
    };

    const { data } = await client.query<BackofficeCommitteesQueryResponse, BackofficeCommitteesQueryVariables>({
      query: BACKOFFICE_COMMITTEES_QUERY,
      variables,
      fetchPolicy: "no-cache"
    });

    const connection = data.backofficeCommittees;
    committees.push(
      ...(connection?.nodes ?? []).map((node) => ({
        id: node.id,
        name: node.name
      }))
    );
    pages += 1;

    const hasNextPage = Boolean(connection?.pageInfo.hasNextPage);
    const nextAfter = connection?.pageInfo.endCursor ?? undefined;
    if (!hasNextPage) {
      return { committees, truncated };
    }
    if (!nextAfter || nextAfter === after) {
      return { committees, truncated: true };
    }
    after = nextAfter;
  }

  truncated = true;
  return { committees, truncated };
}
